import { useRef, useEffect, useContext, createContext } from 'react';
import type { Breakpoint } from '@mui/material/styles';
import type { ContainerProps } from '@mui/material/Container';

import { mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';

import { useSettingsContext } from 'src/components/settings';
import { syncToastSpacing } from 'src/components/snackbar/customToast';

import { layoutClasses } from '../core/classes';

// ----------------------------------------------------------------------

const DashboardContentParentContext = createContext(false);

export type DashboardContentProps = ContainerProps & {
  layoutQuery?: Breakpoint;
  disablePadding?: boolean;
  dataTestId?: string;
};

export function DashboardContent({
  sx,
  children,
  className,
  disablePadding,
  maxWidth = 'lg',
  layoutQuery = 'lg',
  dataTestId,
  ...other
}: DashboardContentProps) {
  const settings = useSettingsContext();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastToastSpacing = useRef<{ marginLeft: string | null; marginRight: string | null }>({
    marginLeft: null,
    marginRight: null,
  });
  const frameIdRef = useRef<number | null>(null);
  const hasParentDashboardContent = useContext(DashboardContentParentContext);
  const isRootDashboardContent = !hasParentDashboardContent;

  const isNavHorizontal = settings.state.navLayout === 'horizontal';

  useEffect(() => {
    if (!isRootDashboardContent || typeof window === 'undefined') {
      return;
    }

    const measureToastSpacing = () => {
      if (!containerRef.current) {
        return;
      }

      const element = containerRef.current;
      const { left, right } = element.getBoundingClientRect();
      const computedStyles = window.getComputedStyle(element);
      const paddingLeft = parseFloat(computedStyles.paddingLeft || '0') || 0;
      const paddingRight = parseFloat(computedStyles.paddingRight || '0') || 0;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;

      const leftOffset = left + paddingLeft - 16; // 16px is the default spacing for toasts
      const rightOffset = viewportWidth - (right - paddingRight) - 16;

      const leftOffsetValue = `${Math.max(leftOffset, 0)}px`;
      const rightOffsetValue = `${Math.max(rightOffset, 0)}px`;

      const hasChanged =
        lastToastSpacing.current.marginLeft !== leftOffsetValue ||
        lastToastSpacing.current.marginRight !== rightOffsetValue;

      if (!hasChanged) {
        return;
      }

      lastToastSpacing.current = {
        marginLeft: leftOffsetValue,
        marginRight: rightOffsetValue,
      };

      syncToastSpacing({
        marginLeft: leftOffsetValue,
        marginRight: rightOffsetValue,
      });
    };

    const scheduleToastSpacingUpdate = () => {
      if (frameIdRef.current !== null) {
        return;
      }

      frameIdRef.current = window.requestAnimationFrame(() => {
        frameIdRef.current = null;
        measureToastSpacing();
      });
    };

    scheduleToastSpacingUpdate();

    const handleWindowResize = () => scheduleToastSpacingUpdate();
    window.addEventListener('resize', handleWindowResize);

    let resizeObserver: ResizeObserver | null = null;

    if ('ResizeObserver' in window && containerRef.current) {
      resizeObserver = new window.ResizeObserver(() => scheduleToastSpacingUpdate());
      resizeObserver.observe(containerRef.current);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      if (frameIdRef.current !== null) {
        window.cancelAnimationFrame(frameIdRef.current);
        frameIdRef.current = null;
      }
      resizeObserver?.disconnect();
    };
  }, [
    disablePadding,
    isRootDashboardContent,
    layoutQuery,
    maxWidth,
    settings.state.compactLayout,
    settings.state.navLayout,
  ]);

  return (
    <DashboardContentParentContext.Provider value>
      <Container
        ref={containerRef}
        data-test-id={dataTestId}
        className={mergeClasses([layoutClasses.content, className])}
        maxWidth={settings.state.compactLayout ? maxWidth : false}
        sx={[
          (theme) => ({
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            pt: 'var(--layout-dashboard-content-pt)',
            pb: 'var(--layout-dashboard-content-pb)',
            [theme.breakpoints.up(layoutQuery)]: {
              px: 'var(--layout-dashboard-content-px)',
              ...(isNavHorizontal && { '--layout-dashboard-content-pt': '40px' }),
            },
            ...(disablePadding && {
              p: {
                xs: 0,
                sm: 0,
                md: 0,
                lg: 0,
                xl: 0,
              },
            }),
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        {children}
      </Container>
    </DashboardContentParentContext.Provider>
  );
}

// ----------------------------------------------------------------------

export const VerticalDivider = styled('span')(({ theme }) => ({
  width: 1,
  height: 10,
  flexShrink: 0,
  display: 'none',
  position: 'relative',
  alignItems: 'center',
  flexDirection: 'column',
  marginLeft: theme.spacing(2.5),
  marginRight: theme.spacing(2.5),
  backgroundColor: 'currentColor',
  color: theme.vars.palette.divider,
  '&::before, &::after': {
    top: -5,
    width: 3,
    height: 3,
    content: '""',
    flexShrink: 0,
    borderRadius: '50%',
    position: 'absolute',
    backgroundColor: 'currentColor',
  },
  '&::after': { bottom: -5, top: 'auto' },
}));
